import _ from "underscore";
import DriveView from "./../DriveView";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { Dialog, offsetArrowUp, IDialogButton } from "./framework/dialog";
import { generateChecklist, Item } from "./framework/checklist";
import { sanitizeUserName } from "../utils/users";
import User from "../model/User";
import { getDebugState } from "../utils/debug";
import constants from "../constants";
import { provideFeedback, handleApi } from "../utils/error";
import { isNoVehicle, getCurrentVehicle, clearCurrentVehicle } from "../utils/device";
import Notifications from "../utils/notifications";
import { getHomeHash, isDriverViewingInspectionMode, isDriverOnAnyWorkflow } from "../utils/uri";
import rison from "rison";
import { createElement, jsonClone } from "../utils/vanilla";
import { MobileActions } from "../store/mobile/actions";
import { PageLevel, DriverActionTypes, IMobilePageState, NetworkStates, LoginRequiredStatus } from "../store/mobile/types";
import toastr, { ToastOrigins } from "../utils/toastr";
import { observeStore } from "../utils/store";
import { WorkdayType } from "../store/data/types";
import { IWorkflowState } from "./workflow";
import { goBack } from "../utils/helpers";
import { getCustomExemptHOSAbbreviation, getTranslatedStatus } from "../utils/hosHelpers";
import { clearTempAssetsFromState } from "./assets/helper";
import Messages from "../model/Messages";
import pageHTML from "./topbar.tmpl";
import userMenuHTML from "./topbar.userMenu.tmpl";
import statusMenuHTML from "./topbar.statusMenu.tmpl";
import notificationsHTML from "./topbar.notifications.tmpl";
import statusMenuHelpHTML from "./topbar.statusMenu.help.tmpl";
import { Source, error, log, Event as EventType, event as loggerEvent } from "../utils/logger";
import Spinner from "../utils/spinner";
import { isInCanada, isAutomaticLog } from "hos-tools";
import { DeviceMovementActions } from "../store/deviceMovement/actions";
import { FeatureFlagName } from "../utils/featureFlagName";
import { hasLockedRoadsideSession } from "./inspection/inspectionHelper";

export const toastOnlineId = "online-status-popup";
export const toastOfflineId = "offline-status-popup";

export default class TopbarView extends DriveView<Backbone.Model> {

    protected strings = [
        "Vehicle",
        "connected",
        "disconnected",
        "GPS",
        "The network connection has been restored.",
        "Connection found",
        "The ELD server is available.",
        "Cannot interact with the ELD while the vehicle is in motion.",
        "Connection lost. Make sure the device is connected to the Internet.",
        "User options",
        "User options for {0}",
        "{0} Connection Help",
        "OFF",
        "SB",
        "D",
        "ON",
        "PC",
        "YM",
        "WT"
    ];
    private readonly template = _.template(pageHTML);
    private readonly userMenuTemplate = _.template(userMenuHTML);
    private readonly statusMenuTemplate = _.template(statusMenuHTML);
    private readonly statusMenuHelpTemplate = _.template(statusMenuHelpHTML);
    private readonly notificationsTemplate = _.template(notificationsHTML);
    private activeMenuItem;
    private currentUser: User;
    private hardwareIdMatch = true;
    private inspectLogChange = false;
    private showNetworkMessage = false;
    private showNetworkMessageTask?: number;
    private showNetworkToastMessageTask: any;
    private previousKnownStateOnline: NetworkStates = NetworkStates.ONLINE;
    private isDvirLogRemoved = false;
    private previousDriving?: boolean | undefined;
    private statusDialog: Dialog | null = null;

    constructor(options) {
        super(options);

        this.activeMenuItem = null;
        this.currentUser = new User(this.app.userService.getCurrentUser());
        this.previousDriving = this.app.store.getState().data.driving;
        this.storeUnsubscribe = this.app.observeStore(state => {
            return [
                state.data.driving,
                state.mobile.eldDiagnostic,
                state.mobile.eldMalfunction,
                state.mobile.newUpcomingViolation,
                state.mobile.gpsConnected,
                state.mobile.ioxAttached,
                state.mobile.language,
                state.mobile.online,
                state.mobile.deviceCommunicating,
                state.mobile.charging,
                state.mobile.pageState,
                state.data.workday,
                state.mobile.pcLimitWarningMessage,
                state.mobile.pendoResourceCenterAvailable,
                state.data.debugMode
            ];
        }, this.render.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.UserUpdate, (user) => {
            // When this render is called by the Dispatcher (after user update), we need to update the cache for this.strings translations.
            this.translate(user.language)
                .then(() => {
                    this.render();
                })
                .catch((e) => {
                    handleApi(e);
                });
        });
        this.listenTo(dispatcher, DispatcherEvents.NotificationsChange, this.render.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.HardwareIdCheck, this.hardwareIdFromIoxCheck.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.DvirLogSet, (logId, isRemove) => {
            this.serverLogUpdate(logId, isRemove);
        });
        this.listenTo(dispatcher, DispatcherEvents.HideTopbarMessage, () => {
            this.inspectLogChange = false;
            this.isDvirLogRemoved = false;
        });
        observeStore(this.app.store, state => state.mobile.online, this.doNetworkMessageTasks.bind(this));
        observeStore(this.app.store, state => state.mobile.ioxAttached, this.clearNetworkMessageTasks.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.SyncComplete, this.render.bind(this));

        if (window.androidCheckmate) {
            Notifications.add("oldContainerVersion", {
                message: "This version of Drive is outdated and will not function correctly. Please update the app or contact your administrator ASAP.",
                critical: true
            }, this.currentUser.id);
        }
    }

    static sendFeedback() {
        provideFeedback();
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .topbar__title": this.titleToggle,
            "click #topbar-user": this.openUserMenu,
            "click #topbar-status": this.openStatusMenu,
            "click #topbar-debug": TopbarView.sendFeedback,
            "click #topbar-notifications": this.openNotifications,
            "click #topbar-driver-asset-selection-cancelbutton": this.cancelLoginLogout,
            "click .malfunctions": this.openMalfunctionsPage,
            "click .HOSViolaton": this.dismissTopBarWarning,
            "click .DVIRCaseUpdate": this.openDvirPage,
            "click .start-workday": this.startWorkday,
            "click .PCLimitWarning": this.dismissPCLimitWarning,
            "click #topbar-selfhelp-button": this.openHelpAndSupport,
            "click .stoppedDriving": this.openHosStatusPage
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        let isInspection = isDriverViewingInspectionMode();
        let hidingIcons = this.hidingIcons();
        let isLocked = this.getPageState().isLocked;
        // MYG-144514: the selector UI moved out of the topbar into the inspection/ELD
        // report views, but the topbar title still needs to render in the inspection language
        // while the driver is viewing Roadside Check.
        let needTranslate = isInspection;
        let currentAppTitle = this.getPageState().title;
        let userId = this.currentUser.id;
        let title = currentAppTitle;
        let isAddin = (this.getPageState().activeSection || "").toLowerCase().indexOf("addin") !== -1;
        let isViewingInspectionMode = isDriverViewingInspectionMode();

        let isWhiteLabelWithIcon = false;
        if (window.whitelabel === "shell" || window.whitelabel === "lytx" || window.whitelabel === "kia" || window.whitelabel === "ford") {
            isWhiteLabelWithIcon = true;
        }

        this.currentUser = new User(this.app.userService.getCurrentUser());
        let userName = this.currentUser.getFullName();
        let userOptionsLabel = this.getString("User options"); // Default value


        void this.display(this.template({
            title,
            noTranslate: (title === constants.APP_NAME) || isAddin,
            showBackButton: this.isShowingBackButton(),
            isInspection,
            isWorkflow: this.isWorkflow(),
            showExit: isViewingInspectionMode,
            showCancel: (this.isLogout() && !isViewingInspectionMode) || this.isChangingVehicle(),
            isChangingAssets: this.isChangingVehicle(),
            doingDvir: this.isDoingDvir(),
            hidingIcons,
            userOptionsLabel,
            userName,
            initials: this.currentUser.getInitials(),
            urgentStatus: !this.app.isFullyConnected || !this.hardwareIdMatch,
            notificationsCount: Notifications.count(userId),
            hasUrgentNotifications: Notifications.hasCritical(userId),
            activeMenuItem: this.activeMenuItem,
            isLocked,
            debugMode: this.app.store.getState().data.debugMode,
            urgentMessage: this.getUrgentMessage(),
            isWhiteLabelled: !!window.whitelabel,
            isWhiteLabelWithIcon: isWhiteLabelWithIcon,
            showPendoResourceCenter: this.app.store.getState().mobile.pendoResourceCenterAvailable
        }), needTranslate ? this.app.store.getState().mobile.language : undefined).then(() => {
            if (this.activeMenuItem === "status") {
                this.openStatusMenu();
            }
        });
        return this;
    }

    countNotifications(notifications) {
        return notifications.reduce((counter, notification) => {
            let amount = counter;

            if (notification.type === "messaging") {
                amount += notification.messages ? notification.messages.length : 1;
            } else {
                amount++;
            }

            return amount;
        }, 0);
    }

    getUrgentMessage() {
        if (isDriverOnAnyWorkflow() || isDriverViewingInspectionMode()) {
            // Should not show malfunction/diagnostics bar on workflow, otherwise the user could navigate directly to the malfunctions page and skip it
            return `${ this.getBrowserSupportMessage() }
                ${ this.getConnectionMessage() }`;
        }
        return `${ this.getBrowserSupportMessage() }
            ${ this.getConnectionMessage() }
            ${ this.getEldJurisdictionMessage() }
            ${ this.getPCLimitWarningMessage() }
            ${ this.getLogChangeMessage() }
            ${ this.getWorkdayMessage() }
            ${ this.getDrivingStatusMessage() }`;
    }

    generateAlert(className: string, text: string) {
        return `<div class="topbar__urgent ${ className }" role="alert">${ text }</div>`;
    }

    generateAlertWithLink(className: string, text: string, isUnlocked: boolean, message: string) {
        let hrefTag = isUnlocked ? "href=\"javascript:void(0)\"" : "";
        return `<a ${ hrefTag } class="topbar__urgent ${ className }"><span>${ text }</span> <span>${ message }</span></a>`;
    }

    getBrowserSupportMessage() {
        if (navigator.userAgent.match(/MSIE/i) || navigator.userAgent.match(/Trident/i)) {
            return this.generateAlert("alert-yellow", "Internet Explorer is not supported. Please use a modern browser.");
        }
        return "";
    }

    getConnectionMessage() {
        let appState = this.app.store.getState();
        if (this.currentUser.id && this.showNetworkMessage && !appState.mobile.ioxAttached) {
            return this.generateAlert("alert-red", "Connection lost. Make sure the device is connected to the Internet.");
        } else if (this.currentUser.id && getCurrentVehicle() && appState.mobile.deviceCommunicating === false && !isNoVehicle() && !appState.mobile.ioxAttached) {
            return this.generateAlert("alert-red", "No connection to the vehicle. Ensure that the telematics device is communicating.");
        }
        return "";
    }

    getEldJurisdictionMessage() {
        let appState = this.app.store.getState();
        let message = "";
        if (appState.data.ELDJurisdiction !== "EWD") {
            let isUnlocked = !this.getPageState().isLocked;
            let tapDetailsMessage = isUnlocked ? "Tap for details." : "";
            if ((appState.mobile.eldDiagnostic || appState.mobile.diagnosticViewed === false) && appState.mobile.eldMalfunction) {
                message = this.generateAlertWithLink(
                    "alert-red malfunctions",
                    "Your Electronic Logging Device includes diagnostics and malfunctions. You may be in non-compliant status.",
                    isUnlocked,
                    tapDetailsMessage
                );
            } else if (appState.mobile.eldDiagnostic || appState.mobile.diagnosticViewed === false) {
                message = this.generateAlertWithLink("alert-yellow malfunctions", "Your ELD has diagnostics.", isUnlocked, tapDetailsMessage);
            } else if (appState.mobile.eldMalfunction) {
                message = this.generateAlertWithLink("alert-red malfunctions", "Your ELD is malfunctioning.", isUnlocked, tapDetailsMessage);
            }

            if (appState.mobile.newUpcomingViolation) {
                let hrefTag = isUnlocked ? "href=\"javascript:void(0)\"" : "";
                message += `<a ${ hrefTag } class="topbar__urgent alert-yellow HOSViolaton">You have an upcoming HOS violation.</a>`;
            }
        }
        return message;
    }

    getPCLimitWarningMessage() {
        let appState = this.app.store.getState();
        if (appState.mobile.pcLimitWarningMessage) {
            let isUnlocked = !this.getPageState().isLocked;
            let tapDismissMessage = isUnlocked ? "Tap to dismiss." : "";
            return this.generateAlertWithLink("alert-red PCLimitWarning", appState.mobile.pcLimitWarningMessage, isUnlocked, tapDismissMessage);
        }
        return "";
    }

    getLogChangeMessage() {
        if (this.inspectLogChange) {
            let isUnlocked = !this.getPageState().isLocked;
            let tapChangesMessage = isUnlocked ? "Tap to see changes." : "";
            if (this.isDvirLogRemoved) {
                return this.generateAlertWithLink("alert-yellow DVIRCaseUpdate", "This log has been removed by an Administrator.", isUnlocked, tapChangesMessage);
            }
            return this.generateAlertWithLink("alert-yellow DVIRCaseUpdate", "This log has been updated by an Administrator.", isUnlocked, tapChangesMessage);
        }
        return "";
    }

    getWorkdayMessage() {
        let appState = this.app.store.getState();
        let isUnlocked = !this.getPageState().isLocked;
        if (!this.app.securityService.can("viewDriverClockInOut")) {
            return "";
        }
        if (isUnlocked && appState.data.workday[this.currentUser.id] === WorkdayType.Stopped) {
            let svgElement = "<svg class='svgIcon big bottom-margin' role='img' aria-hidden='true'><use xlink:href='#drive-clock'></use></svg>";
            return this.generateAlertWithLink("alert-yellow start-workday", `${ svgElement } Tap here to start day`, isUnlocked, "");
        }
        return "";
    }

    getDrivingStatusMessage() {
        const currentDriving = this.app.store.getState().data.driving;

        if (this.previousDriving !== currentDriving) {
            if (currentDriving === false && !this.isLockScreenCleared()) {
                if (this.isUserInAutomaticDriveStatus()) {
                    let isUnlocked = !this.getPageState().isLocked;
                    let tapChangesMessage = isUnlocked ? "Tap to see changes." : "";
                    return this.generateAlertWithLink("alert-yellow stoppedDriving", "You seem to have stopped driving, would you like to end your driving status?", isUnlocked, tapChangesMessage);
                }
            } else {
                this.previousDriving = currentDriving;
            }
        }
        return "";
    }

    isUserInAutomaticDriveStatus() {
        let dutyStatusLogs = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false, this.currentUser.id).filter(hosLog => hosLog.isDashboardLog()));
        let lastLog = this.app.dutyStatusLogService.getLatestLog(dutyStatusLogs);
        return lastLog && isAutomaticLog(lastLog) && (lastLog?.status === "D" || lastLog?.status === "INT_D");
    }

    isLockScreenCleared() {
        let deviceMovement = this.app.store.getState().deviceMovement;
        return deviceMovement.stateUpdate?.source === "USER_EVENT";
    }

    hidingIcons() {
        return this.getPageState().pageLevel === PageLevel.ZERO;
    }

    isShowingBackButton() {
        const pageState = this.getPageState();
        let level = pageState.pageLevel;
        return level === PageLevel.TWO || level === PageLevel.ASSET_SPECIFIC_PAGE || level === PageLevel.SUBMENU_WITH_BACK_BUTTON;
    }

    isWorkflow() {
        return this.isLogin() || this.isLogout() || this.isChangingVehicle();
    }

    isLogin() {
        return this.getPageState().pageLevel === PageLevel.FORCE_LOGOUT;
    }

    isLogout() {
        return this.getPageState().pageLevel === PageLevel.RETURN_DASHBOARD;
    }

    isChangingVehicle() {
        return this.getPageState().pageLevel === PageLevel.RETURN_ASSETS;
    }

    isDoingDvir() {
        return this.getPageState().pageLevel === PageLevel.ASSET_SPECIFIC_PAGE;
    }

    isAddingCoDriver() {
        return this.app.store.getState().mobile.loginRequired.status === LoginRequiredStatus.AddCoDriver;
    }

    hardwareIdFromIoxCheck(isMatching) {
        if (this.hardwareIdMatch !== isMatching) {
            this.hardwareIdMatch = isMatching;
            this.render();
        }
    }

    titleToggle() {
        if (!this.hidingIcons() && !this.isWorkflow()) {
            if (this.getPageState().isLocked) {
                let spinner = new Spinner();
                spinner.spin(document.getElementById("title-x-icon"));
                this.app.screenHandler.canClearLockScreen(false).then((result: boolean) => {
                    if (result) {
                        loggerEvent(EventType.MOTIONCHANGE, "Manual lock status: Executing", this.app.userService.getCurrentUser());
                        this.clearDriveLock();
                    } else {
                        loggerEvent(EventType.MOTIONCHANGE, "Manual lock status: Rejected", this.app.userService.getCurrentUser());
                        toastr.info(this.getString("Cannot interact with the ELD while the vehicle is in motion."));
                    }
                    spinner.stop();
                }).catch(e => {
                    spinner.stop();
                    error(Source.UI, `LockScreen error ${ e }`);
                    this.clearDriveLock();
                });
            } else if (this.isShowingBackButton()) {
                if (this.getPageState().pageLevel === PageLevel.SUBMENU_WITH_BACK_BUTTON) {
                    let previousPage = this.app.store.getState().mobile.previousPage;
                    if (previousPage) {
                        this.app.router.navigate(previousPage, true);
                    } else {
                        this.app.router.navigate(getHomeHash(), true);
                    }
                } else {
                    goBack();
                }
            }
        }
    }

    cancelLoginLogout() {
        const pageLevel = this.getPageState().pageLevel;

        this.app.store.dispatch(MobileActions.setDriverActionNecessary({
            isNecessary: false,
            actionType: DriverActionTypes.Workflow
        }));
        clearTempAssetsFromState(this.app.store, this.app.userService);

        if (pageLevel === PageLevel.FORCE_LOGOUT) {
            // We don't do a session.close here because we haven't created any logs at this point, and therefore no need for us to set an OFF log
            loggerEvent(EventType.DEBUG_LOGINVIEW_NAVIGATE, `cancelLoginLogout button pressed`);
            this.app.router.navigate("login,(loggingOut:true)", true);
        } else if (pageLevel === PageLevel.RETURN_ASSETS) {
            this.app.router.navigate("assets/main", true);
        } else if (isDriverViewingInspectionMode()) {
            dispatcher.trigger(DispatcherEvents.ExitInspectionMode);
        } else {
            // Cancel the logout before the sync
            this.app.router.navigate(getHomeHash(), true);
        }
    }

    openHosStatusPage() {
        this.previousDriving = this.app.store.getState().data.driving;
        let currentPage = Backbone.history.fragment;
        if (currentPage && currentPage.indexOf("hos/tabs/main/main") === -1) {
            this.app.router.navigate("hos", true);
        } else {
            this.render();
        }
    }

    openMalfunctionsPage() {
        if (!this.getPageState().isLocked) {
            this.app.router.navigate("hos/malfunctions", true);
        }
    }

    openDvirPage() {
        this.inspectLogChange = false;
        this.isDvirLogRemoved = false;
        let currentPage = Backbone.history.fragment;
        if (currentPage && currentPage.indexOf("dvir/main") !== -1) {
            Backbone.history.fragment = null;
        }
        this.app.router.navigate("dvir/main", true);
    }

    startWorkday() {
        let nextPageState: IWorkflowState = {};
        let driver = this.app.userService.getDriversSeatUser();
        if (driver && driver.id !== this.currentUser.id) {
            nextPageState = { coDriver: true };
        }
        this.app.router.toWorkflow(false, nextPageState);
    }

    dismissTopBarWarning() {
        if (!this.getPageState().isLocked) {
            this.app.router.navigate("hos", true);
        }
        this.app.store.dispatch(MobileActions.setNewUpcomingViolation(false));
    }

    isNotUsingVehicle(lastStatuses) {
        if (this.app.store.getState().data.driving) {
            return false;
        }

        let users = Object.keys(lastStatuses);
        let statuses = ["D", "PC", "PC_Exempted", "WT", "YM"];

        return users.every(user => statuses.indexOf(lastStatuses[user]) === -1);
    }

    dismissPCLimitWarning() {
        this.app.store.dispatch(MobileActions.setPcLimitWarningMessage(undefined));
        let userId = !this.app.userService.getCurrentUser() ? undefined : this.app.userService.getCurrentUser()?.id;
        let pcLimitDetails = this.app.dutyStatusLogService.reachedDailyPCDrivingLimit(userId);
        let logToEdit = pcLimitDetails.latestPCLog;
        if (logToEdit) {
            let annotation = "Personal Conveyance exemption limit warning was dismissed by the driver.";
            logToEdit.addAnnotation(this.app.hosUtils.getNewAnnotation(annotation, this.currentUser));
            void this.app.sync.dutyStatusLogs.setItem(jsonClone(logToEdit));
        }
    }

    openHelpAndSupport() {
        const resourceCenter = window.pendo?.BuildingBlocks?.BuildingBlockResourceCenter?.getResourceCenter?.();
        if (resourceCenter && typeof resourceCenter.show === "function") {
            resourceCenter.show();
        }
    }

    openUserMenu() {
        let users = this.app.userService.getSortedUsers(true);
        let lastStatuses = {};
        let activeUser = new User(this.app.userService.getCurrentUser());
        let usingNoVehicle = isNoVehicle();
        let canAccessHos = this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && usingNoVehicle);

        if (!activeUser || !activeUser.id) {
            // Panic! (Or just try logging in again)
            loggerEvent(EventType.DEBUG_LOGINVIEW_NAVIGATE, "openUserMenu: No active user found, redirecting to login.");
            this.app.router.navigate("login,(loggingIn:!t)", true);
            return;
        }

        this.app.network.checkBridge();

        users = users.filter(user => user.id !== activeUser.id);
        users.unshift(activeUser);

        if (canAccessHos) {
            users.forEach((user) => {
                let tempDutyStatusLogs = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false, user.id).filter(hosLog => hosLog.isDashboardLog()));
                let tempLastLog = this.app.dutyStatusLogService.getLatestLog(tempDutyStatusLogs);
                lastStatuses[user.id] = tempLastLog && tempLastLog.status ? tempLastLog.status : "OFF";
            });
        }

        // Create translated statuses map
        let translatedStatuses = {};
        let customExemptHOSAbbreviation = getCustomExemptHOSAbbreviation(this.app.store.getState().systemSettings);
        Object.keys(lastStatuses).forEach((userId) => {
            let status = lastStatuses[userId];
            translatedStatuses[userId] = getTranslatedStatus(status, customExemptHOSAbbreviation, this.getString.bind(this));
        });

        let activeDriver = this.app.userService.getDriversSeatUser();
        let activeDriverName = activeDriver ? sanitizeUserName(activeDriver.name) : "";
        let state = getDebugState();
        let maxUsers = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.MAX_DRIVERS_LIMIT_INCREASE) ?
                constants.SESSION.MAX_LOGGED_IN_USERS_OVERRIDE :
                constants.SESSION.MAX_LOGGED_IN_USERS;


        let userDialog = new Dialog("user-dialog", this.userMenuTemplate({
            isLocked: state && !state.ALLOW_LOGOUT_WHILE_DRIVING && this.getPageState().isLocked,
            users,
            customExemptHOSAbbreviation: customExemptHOSAbbreviation,
            lastStatuses,
            translatedStatuses,
            canAccessHos,
            activeDriver: activeDriverName,
            activeUser,
            canAdd: (users.length < maxUsers) && this.app.store.getState().data.ELDJurisdiction !== "EWD",
            showDriversSeat: users.length > 1 && !isNoVehicle(),
            allowDriversSeatChange: this.isNotUsingVehicle(lastStatuses) && !this.isAddingCoDriver(),
            hasNoVehicle: isNoVehicle(),
            isWorkflow: this.isWorkflow(),
            isLogin: this.isLogin(),
            isRoadsideCheck: hasLockedRoadsideSession(this.app.store),
            showFeedback: !!constants.FEEDBACK.EXTERNAL_SUPPORT_URL
        }), this.translator, "User Menu", undefined, {
            isNightMode: this.app.store.getState().data.nightMode,
            alignToElement: "#topbar-user",
            hideTitle: true
        });

        userDialog.onRemove(() => {
            let userButton: HTMLElement | undefined = this.app.el?.querySelector("#topbar-user");
            this.activeMenuItem = null;
            userButton && userButton.classList.remove("topbar__item_active");
            userButton?.setAttribute("aria-expanded", "false");
        });

        void userDialog.open().then((renderedUserDialog) => {
            if (!renderedUserDialog.el){
                return;
            }
            let userButton: HTMLElement | undefined = this.app.el?.querySelector("#topbar-user");

            dispatcher.trigger(DispatcherEvents.UIUserMenu);
            this.activeMenuItem = "user";
            userButton && userButton.classList.add("topbar__item_active");
            userButton?.setAttribute("aria-expanded", "true");

            let logoutButton = renderedUserDialog.el.querySelector("#topbar-user-logout");
            logoutButton && logoutButton.addEventListener("click", event => {
                let target = <HTMLElement>event.currentTarget;
                if (target.classList.contains("button_disabled")) {
                    return;
                }

                this.logInteraction(event);
                userDialog && userDialog.remove();
                if (this.isLogin()) {
                    this.cancelLoginLogout();
                } else {
                    this.app.router.toWorkflow(true);
                }
            }, false);

            let feedbackButton = renderedUserDialog.el.querySelector("#topbar-user-feedback");
            feedbackButton && feedbackButton.addEventListener("click", event => {
                this.logInteraction(event);
                userDialog && userDialog.remove();

                // Open feedback dialog
                dispatcher.trigger(DispatcherEvents.ProvideFeedback);
            }, false);

            let userListElements = renderedUserDialog.el.querySelectorAll("#driver-menu-users-list > li");
            Array.prototype.forEach.call(userListElements, userElement => {
                userElement.addEventListener("click", event => {
                    let userId = event.currentTarget.getAttribute("data-id");
                    if (userId && userId.length > 0) {
                        this.logInteraction(event);
                        this.app.router.navigate(`login,(switchUser:${ userId })`, true);
                    }
                }, false);
            });

            let addUserButton = renderedUserDialog.el.querySelector("#topbar-user-add");
            addUserButton && addUserButton.addEventListener("click", event => {
                let target = <HTMLElement>event.currentTarget;

                if (!target.classList.contains("button_disabled") && (!isInCanada(this.app.hosUtils.getLatestHosRuleSetIdShortened()) || !this.getPageState().isLocked)) {
                    userDialog && userDialog.remove();
                    this.logInteraction(event);
                    this.app.router.navigate("login,(addCoDriver:true)", true);
                }
            }, false);

            let switchUserButton = renderedUserDialog.el.querySelector("#topbar-user-switch-primary");
            switchUserButton && switchUserButton.addEventListener("click", event => {
                let target = <HTMLElement>event.currentTarget;

                if (!target.classList.contains("button_disabled")) {
                    userDialog && userDialog.remove();
                    this.logInteraction(event);
                    this.openActiveDriverSwitcher(users, activeDriverName);
                }
            }, false);

            userButton && offsetArrowUp(userButton, "user-dialog");
        });
    }

    openStatusMenu() {
        if (this.statusDialog) {
            this.statusDialog.remove();
            this.statusDialog = null;
        }

        this.app.network.checkBridge();
        let status = this.app.store.getState().mobile;
        let vehicleConnectedMessage = `${ this.getString("Vehicle") } ${ this.getString(status.deviceCommunicating || status.ioxAttached ? "connected" : "disconnected") }`;
        let gpsConnectedMessage = `${ this.getString("GPS") } ${ this.getString(status.gpsConnected || status.ioxAttached ? "connected" : "disconnected") }`;
        let itemOnlineHelpLabel = this.getString("Help");

        this.statusDialog = new Dialog("status-dialog", this.statusMenuTemplate({
            status: status,
            online: status.online === NetworkStates.ONLINE,
            appName: constants.APP_NAME,
            hasNoVehicle: isNoVehicle(),
            vehicleConnectedString: vehicleConnectedMessage,
            gpsConnectedString: gpsConnectedMessage,
            itemOnlineHelpLabel: itemOnlineHelpLabel
        }), this.translator, "Status", undefined, {
            alignToElement: "#topbar-status",
            isNightMode: this.app.store.getState().data.nightMode,
            hideTitle: true
        });

        this.statusDialog.onRemove(() => {
            let statusButton: HTMLElement | undefined = this.app.el?.querySelector("#topbar-status");
            this.activeMenuItem = null;
            statusButton && statusButton.classList.remove("topbar__item_active");
            statusButton?.setAttribute("aria-expanded", "false");
        });

        void this.statusDialog.open().then((renderedStatusDialog) => {
            if (!renderedStatusDialog.el){
                return;
            }
            let statusButton: HTMLElement | undefined = this.app.el?.querySelector("#topbar-status");

            let urgentRows = renderedStatusDialog.el.querySelectorAll("#status-menu button");
            Array.prototype.forEach.call(urgentRows, urgentRow => {
                urgentRow.addEventListener("click", event => {
                    this.logInteraction(event);
                    let target = <HTMLElement>event.currentTarget;
                    let statusId = target.getAttribute("id");

                    let helpDialog = new Dialog(
                        "help-dialog",
                        this.statusMenuHelpTemplate({ statusId }),
                        this.translator,
                        "Help",
                        [
                            {
                                text: "OK",
                                id: "help-dialog-ok",
                                callback: () => {
                                    helpDialog.remove();
                                }
                            }
                        ],
                        {
                            showCloseIcon: true,
                            isNightMode: this.app.store.getState().data.nightMode
                        }
                    );

                    helpDialog.onRemove(() => {
                        this.statusDialog?.remove();
                    });

                    void helpDialog.open();
                }, false);
            });

            this.activeMenuItem = "status";
            statusButton?.setAttribute("aria-expanded", "true");
            statusButton?.classList.add("topbar__item_active");

            statusButton && offsetArrowUp(statusButton, "status-dialog");
        });
    }

    openNotifications() {
        let userNotifications = Notifications.getVisible(this.currentUser.id);
        let isLocked = this.getPageState().isLocked;

        userNotifications.sort((x, y) => (x.critical === y.critical) ? 0 : (x.critical ? -1 : 1));

        let events = userNotifications.find((notification) => notification.type === "system" && notification.id === "unreadEvents");
        let messages = userNotifications.find((notification) => notification.type === "messaging" && notification.id === "unreadMessages");
        let otherMessages = userNotifications.filter((notification) => notification.type === "messaging" && notification.id !== "unreadMessages");

        let notificationsDialog = new Dialog(
            "notifications-dialog",
            this.notificationsTemplate({
                notifications: userNotifications,
                id: this.currentUser.id,
                eventsNotification: events,
                messagesNotification: messages,
                otherMessagesNotification: otherMessages
            }),
            this.translator,
            "Notifications",
            undefined,
            {
                alignToElement: "#topbar-notifications",
                isNightMode: this.app.store.getState().data.nightMode,
                minWidth: 200,
                hideTitle: true
            }
        );

        notificationsDialog.onRemove(() => {
            let notificationsButton = document.getElementById("topbar-notifications");
            notificationsButton && notificationsButton.classList.remove("topbar__item_active");
            notificationsButton?.setAttribute("aria-expanded", "false");
            this.activeMenuItem = null;
        });

        void notificationsDialog.open()
            .then(() => {
                let elements = document.querySelectorAll("#notifications-menu a");

                Array.prototype.forEach.call(elements, element => {
                    let notificationClickHandler = event => {
                        this.logInteraction(event);
                        let target = event.currentTarget;
                        let notificationId = target && target.getAttribute("data-id");
                        notificationId && Notifications.click(this.app.router, notificationId, !isLocked, this.currentUser.id);
                        notificationsDialog && notificationsDialog.remove();
                    };

                    element.addEventListener("click", notificationClickHandler, false);
                });

                this.activeMenuItem = "notifications";
                let notificationsButton = document.getElementById("topbar-notifications");
                notificationsButton && notificationsButton.classList.add("topbar__item_active");
                notificationsButton?.setAttribute("aria-expanded", "true");

                offsetArrowUp(notificationsButton, "notifications-dialog");
            });
    }

    getMessages() {
        let messages = new Messages(this.app.store);
        let allMessages = messages.getMessages({
            sortAscending: false,
            groupRouteMessages: true
        });
        return allMessages;
    }

    doNetworkMessageTasks(online?: NetworkStates) {
        let isOnline = online === NetworkStates.ONLINE;
        let previouslyOnline = this.previousKnownStateOnline === NetworkStates.ONLINE;
        if (isOnline === previouslyOnline || this.app.store.getState().mobile.ioxAttached) {
            if (online) {
                this.previousKnownStateOnline = online;
            }
            return; // If an network state change is triggered but the previous state was already the same there is nothing to do or if the IOX is attached
        }
        this.updateDisplayOnlineMessageStatus(online);
        this.updateNetworkToastMessageTask(online, this.previousKnownStateOnline);
        if (online) {
            this.previousKnownStateOnline = online;
        }
    }

    updateDisplayOnlineMessageStatus(online?: NetworkStates): void {
        if (online !== NetworkStates.ONLINE) {
            this.showNetworkMessageTask = setTimeout(() => {
                this.showNetworkMessage = true;
                this.showNetworkMessageTask = undefined;
                this.render();
            }, constants.OFFLINE_MESSAGE_GRACE_PERIOD_MS);
        } else {
            if (this.showNetworkMessageTask) {
                clearTimeout(this.showNetworkMessageTask);
            }
            this.showNetworkMessage = false;
            this.render();
        }
    }

    getOnlineMessage(previousState: NetworkStates) {
        return previousState === NetworkStates.SERVER_OFFLINE ? this.getString("The ELD server is available.") : this.getString("The network connection has been restored.");
    }

    updateNetworkToastMessageTask(online?: NetworkStates, previousState?: NetworkStates): void {
        if (this.showNetworkToastMessageTask) {
            // If already about to display a toast message cancel it, it means the online state is changing too rapidly to display anything meaningful
            clearTimeout(this.showNetworkToastMessageTask);
            this.showNetworkToastMessageTask = null;
        } else if (previousState) {
            this.showNetworkToastMessageTask = setTimeout(() => {
                online === NetworkStates.ONLINE
                    ? toastr.success(
                        this.getOnlineMessage(previousState),
                        this.getString("Connection found"),
                        undefined,
                        { toastId: toastOnlineId, origin: ToastOrigins.NETWORK_CONNECTION_CHANGE }
                    )
                    : toastr.warning(
                        this.getString("Connection lost. Make sure the device is connected to the Internet."),
                        undefined,
                        undefined,
                        {
                            isUserNightMode: this.app.store.getState().data.nightMode,
                            toastId: toastOfflineId,
                            origin: ToastOrigins.NETWORK_CONNECTION_CHANGE
                        }
                    );
                this.showNetworkToastMessageTask = null;
            }, constants.NETWORK_TOAST_MESSAGE_GRACE_PERIOD_MS);
        }
    }

    clearNetworkMessageTasks(): void {
        if (this.app.store.getState().mobile.ioxAttached) {
            clearTimeout(this.showNetworkToastMessageTask);
            clearTimeout(this.showNetworkMessageTask);
            (window as any).networkMessageTimeoutCleared = true;
        }
    }

    clearDriveLock() {
        if (this.app.userService.isDriverUsingUI(this.currentUser.name)) {
            let currentDrivingStatus = this.app.store.getState().data.driving;
            let hadCachedContentView = !!this.app.cachedContentView;

            this.app.store.dispatch(DeviceMovementActions.setStateUpdate({
                isDriving: false,
                source: "USER_EVENT",
                loggingMessage: "User manually clear Lockscreen from login"
            }));

            log(Source.USER_EVENT, `LockScreen: Cleared by user, cachedContentView=${ hadCachedContentView }, wasDriving=${ currentDrivingStatus }`, this.currentUser);

            this.app.api.call("Add", {
                typeName: "Audit",
                entity: {
                    name: "DriveLockUserCleared",
                    comment: `Driving? ${ currentDrivingStatus }`
                }
            });
        }
    }

    serverLogUpdate(incId, isRemove = false) {
        if (window.location.href.indexOf("dvir/inspect,(id:'") !== -1) {
            let currId = window.location.href.split("inspect,(id:'")[1].slice(0, incId !== null ? incId.length : 0);
            if (currId === incId) {
                this.inspectLogChange = true;
                this.isDvirLogRemoved = isRemove;
                this.render();
            }
        }
    }

    openActiveDriverSwitcher(users, activeDriverName) {
        let reportHTML = createElement("div", undefined, "activeDriverSelection");
        let driverChecklistitems: Item[] = users.map(user => {
            let isCurrentDriver = user.name.toLowerCase() === activeDriverName.toLowerCase();
            return new Item({
                id: user.id,
                title: user.getFullName(),
                selected: isCurrentDriver,
                subtitle: isCurrentDriver ? "Current driver" : "",
                userName: user.name
            });
        });
        let switcherDialog: Dialog;
        const dialogId = "active-driver-switcher-dialog";
        let selectedUserName: string | null = activeDriverName;
        let switcherChecklist;

        const cancelSwitchButton: IDialogButton = {
            text: "Cancel",
            callback: () => {
                // If a different driver is selected, perform the switch
                if (selectedUserName && selectedUserName.toLowerCase() !== activeDriverName.toLowerCase()) {
                    const userName = selectedUserName;
                    let userContext = this.app.manager.getCachedContext(userName);
                    this.app.vehicleService.hasAccessToDevice(getCurrentVehicle() as IIdEntity, userContext ? userContext.api : undefined)
                        .then(hasAccess => {
                            if (hasAccess) {
                                this.app.userService.setDriversSeatUser(userName);
                            } else {
                                clearCurrentVehicle();
                                this.showNoAccessToVehicleDialog();
                            }

                            switcherChecklist.remove();
                            switcherDialog.remove();
                        })
                        .catch(err => {
                            if (err.data.type === "NetworkError") {
                                this.app.userService.setDriversSeatUser(userName);
                                switcherChecklist.remove();
                                switcherDialog.remove();
                            } else {
                                handleApi(err);
                            }
                        });
                } else {
                    switcherChecklist.remove();
                    switcherDialog.remove();
                }
            }
        };

        const updateButtonText = () => {
            const isDifferentDriver = selectedUserName && selectedUserName.toLowerCase() !== activeDriverName.toLowerCase();
            const newText = isDifferentDriver ? "Switch" : "Cancel";
            if (cancelSwitchButton.el) {
                cancelSwitchButton.el.textContent = newText;
            }
        };

        switcherChecklist = generateChecklist({
            items: driverChecklistitems,
            isCheckable: true,
            labelledBy: `${ dialogId }-title`,
            selectCallback: item => {
                // Deselect all other items to ensure only one is selected at a time
                switcherChecklist.collection.each(checklistItem => {
                    if (checklistItem !== item) {
                        checklistItem.set("selected", false);
                    }
                });

                selectedUserName = item.get("userName");

                updateButtonText();
            },
            context: this,
            translator: this.translator,
            noTranslate: true,
            el: reportHTML
        });

        let html = createElement("div").appendChild(reportHTML);
        switcherDialog = new Dialog(
            dialogId,
            [html],
            this.translator,
            "Driver's seat",
            [cancelSwitchButton],
            {
                showCloseIcon: true,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void switcherDialog.open().then(() => {
            updateButtonText();
        });
    }

    private showNoAccessToVehicleDialog() {
        let changeVehicleDialog = new Dialog(
            "driver-seat-no-access",
            "The driver does not have access to the current vehicle. Please select another vehicle.",
            this.translator,
            "No Access",
            [{
                id: "reselect-vehicle",
                text: "OK",
                callback: () => {
                    changeVehicleDialog.remove();
                    this.app.router.navigate(`workflow,${ rison.encode({
                        logout: false,
                        step: "selectVehicle",
                        changeVehicle: true
                    }) }`, true);
                }
            }],
            {
                showCloseIcon: false,
                closable: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void changeVehicleDialog.open();
    }

    private getPageState(): IMobilePageState {
        return this.app.store.getState().mobile.pageState;
    }

    private logInteraction(e?: Event) {
        e && dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
    }
}
